import type { ApiResponse, PagedResponseDto } from "@/types/common";
import type {
  AdminStoreBvAdjustDto,
  AdminStoreCreateDto,
  AdminStoreUpdateDto,
  NearbyStoreRequest,
  StoreBvBalanceDto,
  StoreBvHistoryDto,
  StoreDashboardStatsDto,
  StoreListDto,
  StoreResponseDto,
  StoreUpdateDto,
  StoreRegisterDto,
  StoreKycDto,
} from "@/types/store.dto";
import { apiClient, unwrapApiResponse } from "./httpClient";

export const storesService = {
  async registerStore(dto: StoreRegisterDto): Promise<void> {
    const { data } = await apiClient.post<ApiResponse>("/Stores/register", dto);
    if (!data.success) throw new Error(data.message || "Registration failed");
  },
  getNearby(request: NearbyStoreRequest): Promise<PagedResponseDto<StoreListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<StoreListDto>>>("/Stores/nearby", { params: request }),
    );
  },

  getById(id: number): Promise<StoreResponseDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<StoreResponseDto>>(`/Stores/${id}`));
  },

  getMyStore(): Promise<StoreResponseDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<StoreResponseDto>>("/Stores/my-store"));
  },

  getDashboard(storeId: number): Promise<StoreDashboardStatsDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<StoreDashboardStatsDto>>(`/Stores/${storeId}/dashboard`),
    );
  },

  update(storeId: number, dto: StoreUpdateDto): Promise<StoreResponseDto> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<StoreResponseDto>>(`/Stores/${storeId}`, dto),
    );
  },

  getAll(page = 1, pageSize = 20, status?: string, city?: string): Promise<PagedResponseDto<StoreListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<StoreListDto>>>("/Stores", {
        params: { page, pageSize, status, city },
      }),
    );
  },

  getPending(page = 1, pageSize = 20): Promise<PagedResponseDto<StoreListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<StoreListDto>>>("/Stores/pending", {
        params: { page, pageSize },
      }),
    );
  },

  approve(id: number, approve: boolean, rejectionReason?: string): Promise<StoreResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<StoreResponseDto>>(`/Stores/${id}/approve`, {
        approve,
        rejectionReason,
      }),
    );
  },

  suspend(id: number, reason: string): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(`/Stores/${id}/suspend`, JSON.stringify(reason), {
        headers: { "Content-Type": "application/json" },
      }),
    );
  },

  async createByAdmin(dto: AdminStoreCreateDto): Promise<ApiResponse<StoreResponseDto>> {
    const { data } = await apiClient.post<ApiResponse<StoreResponseDto>>("/Stores/admin", dto);
    if (!data.success) throw new Error(data.message || "Request failed");
    return data;
  },

  updateByAdmin(id: number, dto: AdminStoreUpdateDto): Promise<StoreResponseDto> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<StoreResponseDto>>(`/Stores/${id}/admin`, dto),
    );
  },

  deleteByAdmin(id: number): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.delete<ApiResponse<boolean>>(`/Stores/${id}/admin`),
    );
  },
  updateKyc(storeId: number, dto: StoreKycDto): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<boolean>>(`/Stores/${storeId}/kyc`, dto),
    );
  },

  getBvBalance(storeId: number): Promise<StoreBvBalanceDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<StoreBvBalanceDto>>(`/Stores/${storeId}/bv`));
  },

  getBvHistory(storeId: number, page = 1, pageSize = 50): Promise<StoreBvHistoryDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<StoreBvHistoryDto>>(`/Stores/${storeId}/bv/history`, {
        params: { page, pageSize },
      }),
    );
  },

  adjustBv(storeId: number, dto: AdminStoreBvAdjustDto): Promise<StoreBvBalanceDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<StoreBvBalanceDto>>(`/Stores/${storeId}/bv/adjust`, dto),
    );
  },
};
